import {
  HttpBackend,
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpInterceptorFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, firstValueFrom, from, switchMap, throwError } from 'rxjs';
import { AuthService, LoginResponse } from '../services/auth.service';
import { environment } from '../../../environments/environment';

/** URLs públicas de auth: não tentar refresh nem logout forçado em 401 (ex.: login inválido). */
const PUBLIC_AUTH_PATHS = ['/public/app/authentication/'];

/** 401 nestes endpoints pode ser regra de negócio (ex.: senha atual errada) — não renovar sessão. */
const NO_REFRESH_ON_401_PATHS = ['/member/change_password'];

let refreshInFlight: Promise<string> | null = null;

function buildAppHeaders(): HttpHeaders {
  let h = new HttpHeaders({ 'Content-Type': 'application/json' });
  if (environment.jwtSecretKeyApp) {
    h = h.set('X-App-Secret', environment.jwtSecretKeyApp);
  }
  return h;
}

function performTokenRefresh(auth: AuthService, httpBackend: HttpBackend): Promise<string> {
  if (refreshInFlight) {
    return refreshInFlight;
  }
  const rt = auth.getRefreshToken();
  if (!rt) {
    return Promise.reject(new HttpErrorResponse({ status: 401, statusText: 'No refresh token' }));
  }
  const rawHttp = new HttpClient(httpBackend);
  const url = `${environment.apiUrl}/public/app/authentication/refresh_token`;
  refreshInFlight = firstValueFrom(
    rawHttp.post<LoginResponse>(url, { refresh_token: rt }, { headers: buildAppHeaders() })
  )
    .then((res) => {
      if (res.status !== 'SUCCESS' || !res.content?.token) {
        throw new HttpErrorResponse({ status: 401, error: res });
      }
      auth.persistSessionFromRefreshResponse(res);
      return res.content.token;
    })
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const httpBackend = inject(HttpBackend);

  const token = authService.getToken();
  const appSecret = environment.jwtSecretKeyApp;

  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (appSecret) {
    headers['X-App-Secret'] = appSecret;
  }

  const clonedReq =
    Object.keys(headers).length > 0 ? req.clone({ setHeaders: headers }) : req;

  return next(clonedReq).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status !== 401) {
        return throwError(() => err);
      }

      if (PUBLIC_AUTH_PATHS.some((p) => req.url.includes(p))) {
        return throwError(() => err);
      }

      if (NO_REFRESH_ON_401_PATHS.some((p) => req.url.includes(p))) {
        return throwError(() => err);
      }

      if (req.url.includes('/authentication/refresh_token')) {
        authService.logout();
        router.navigate(['/login']);
        return throwError(() => err);
      }

      return from(performTokenRefresh(authService, httpBackend)).pipe(
        switchMap((newToken) => {
          const retryHeaders: Record<string, string> = {
            Authorization: `Bearer ${newToken}`,
          };
          if (appSecret) {
            retryHeaders['X-App-Secret'] = appSecret;
          }
          return next(req.clone({ setHeaders: retryHeaders }));
        }),
        catchError((refreshErr) => {
          authService.logout();
          router.navigate(['/login']);
          return throwError(() => refreshErr);
        })
      );
    })
  );
};
